import { type CSSProperties, useEffect, useRef } from 'react';

/**
 * Inline Cal.com booking embed, hand-rolled around the official vanilla snippet
 * rather than @calcom/embed-react — the package is a thin shim over the same
 * remotely-loaded iframe, and the repo's zero-dependency bias holds.
 *
 * The iframe is the point, not a limitation: same-origin policy means Cal.com's
 * code can't read the portal page (or the session cookie), and when Stripe is
 * enabled, card details exist only inside Cal.com's frame.
 *
 * Each mount site uses its own Cal namespace so event listeners don't
 * cross-talk, and cleanup clears the container so StrictMode's double-mount
 * yields exactly one iframe.
 */

interface CalNamespace {
  (...args: unknown[]): void;
  q?: unknown[];
}

interface CalApi {
  (...args: unknown[]): void;
  ns?: Record<string, CalNamespace>;
  q?: unknown[];
  loaded?: boolean;
}

declare global {
  interface Window {
    Cal?: CalApi;
  }
}

/**
 * Faithful port of Cal.com's official bootstrap snippet: creates the window.Cal
 * command queue synchronously and injects embed.js once. The protocol matters —
 * each namespace is its own function with its OWN queue, the namespace's init
 * call goes into that queue, and the global queue gets an `initNamespace`
 * marker. embed.js replays both on load; deviating from this shape makes it
 * throw "Instruction not FOUND".
 */
function ensureCal(): CalApi {
  if (window.Cal) return window.Cal;
  const push = (api: { q?: unknown[] }, args: unknown) => {
    api.q = api.q ?? [];
    api.q.push(args);
  };
  const cal: CalApi = function (...args: unknown[]) {
    if (!cal.loaded) {
      cal.ns = cal.ns ?? {};
      cal.q = cal.q ?? [];
      const script = document.createElement('script');
      script.src = 'https://app.cal.com/embed/embed.js';
      document.head.appendChild(script);
      cal.loaded = true;
    }
    if (args[0] === 'init') {
      const namespace = args[1];
      const api: CalNamespace = function (...nsArgs: unknown[]) {
        push(api, nsArgs);
      };
      api.q = api.q ?? [];
      if (typeof namespace === 'string') {
        cal.ns![namespace] = cal.ns![namespace] ?? api;
        push(cal.ns![namespace], args);
        push(cal, ['initNamespace', namespace]);
      } else {
        push(cal, args);
      }
      return;
    }
    push(cal, args);
  };
  window.Cal = cal;
  return cal;
}

export interface CalEmbedProps {
  /** Cal.com path, e.g. "liev" or "liev/session-60". */
  calLink: string;
  /** One namespace per mount site so embeds don't share event listeners. */
  namespace: string;
  prefill?: { name?: string; email?: string; notes?: string };
  onBookingSuccessful?: () => void;
  style?: CSSProperties;
}

export function CalEmbed({ calLink, namespace, prefill, onBookingSuccessful, style }: CalEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const onBookedRef = useRef(onBookingSuccessful);
  onBookedRef.current = onBookingSuccessful;

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const cal = ensureCal();
    cal('init', namespace, { origin: 'https://cal.com' });

    // Brand color from the design tokens at runtime — no hardcoded hex.
    const brand = getComputedStyle(document.documentElement).getPropertyValue('--navy-600').trim();

    const ns = cal.ns?.[namespace] ?? cal;
    ns('inline', {
      elementOrSelector: el,
      calLink,
      config: { ...(prefill ?? {}), theme: 'light' },
    });
    if (brand) {
      ns('ui', { cssVarsPerTheme: { light: { 'cal-brand': brand } }, hideEventTypeDetails: false });
    }
    const onBooked = () => onBookedRef.current?.();
    ns('on', { action: 'bookingSuccessful', callback: onBooked });

    return () => {
      ns('off', { action: 'bookingSuccessful', callback: onBooked });
      // StrictMode mounts twice in dev; clearing the container guarantees the
      // remount produces exactly one iframe.
      el.innerHTML = '';
    };
    // Prefill identity is stable per mount site; re-embedding on every render
    // of a parent would tear down the user's in-progress slot selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calLink, namespace]);

  return <div ref={containerRef} style={{ minHeight: 480, ...style }} />;
}
